export const M5_UNIT_2 = {
  id: "m5-u2-dates",
  title: "Unit 2: Dates & Calendar",
  topic: "Talking about dates, birthdays, and important events",
  iconType: "calendar",
  grammarImage: "/grammar/grammar-m5-u2.jpg",
  grammarText: "In English, we use ordinal numbers (1st, 2nd, 3rd) to talk about dates. There are two common formats: US format 'May 10' (said as 'May tenth') and UK format '10th of May' (said as 'the tenth of May'). We use 'was born' to talk about someone's birth date, and 'ago' to say how long before now something happened. Years are usually said in pairs: 1995 = 'nineteen ninety-five', but 2015 can be 'twenty fifteen' or 'two thousand and fifteen'. Understanding dates helps you talk about birthdays, appointments, historical events, and expiration dates.",

  sets: [
    // ===================================================================
    // SET 1: DAYS OF THE WEEK (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u2-s1",
      title: "Set 1: Days of the Week",
      questions: [
        {
          type: "match",
          q: "Match the days to their abbreviations:",
          pairs: [
            { left: "Monday", right: "Mon" },
            { left: "Wednesday", right: "Wed" },
            { left: "Friday", right: "Fri" }
          ],
          a: "completed",
          successtip: "✓ Abbreviations are used in calendars and schedules! 📅"
        },
        {
          type: "mc",
          q: "What day comes after Monday?",
          options: ["Tuesday", "Sunday", "Wednesday"],
          a: "Tuesday",
          successtip: "✓ Mon → Tue → Wed! Remember the order! 📆"
        },
        {
          type: "input",
          q: "The first day of the work week is ___.",
          a: "Monday",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["monday"] },
          successtip: "✓ MONDAY! In most countries, the work week starts Monday! 💼"
        },
        {
          type: "mc",
          q: "Which day comes before Sunday?",
          options: ["Saturday", "Friday", "Monday"],
          a: "Saturday",
          successtip: "✓ Saturday is the last day before Sunday! Weekend! 🎉"
        },
        {
          type: "order",
          q: "Put the days in order:",
          words: ["Monday", "Tuesday", "Wednesday"],
          a: "Monday Tuesday Wednesday",
          tip: "These are the first three days of the week!"
        },
        {
          type: "input",
          q: "The weekend includes Saturday and ___.",
          a: "Sunday",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ SUNDAY! Weekend = Saturday + Sunday! 🏖️"
        },
        {
          type: "mc",
          q: "What day is in the middle of the work week?",
          options: ["Wednesday", "Monday", "Friday"],
          a: "Wednesday",
          successtip: "✓ Wednesday = 'Hump Day' (middle of the week)! 🐪"
        },
        {
          type: "match",
          q: "Match the expressions:",
          pairs: [
            { left: "Today", right: "This day" },
            { left: "Tomorrow", right: "The next day" },
            { left: "Yesterday", right: "The day before" }
          ],
          a: "completed",
          successtip: "✓ Today/Tomorrow/Yesterday are essential time words! ⏰"
        },
        {
          type: "input",
          q: "TGIF means 'Thank God It's ___'.",
          a: "Friday",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ FRIDAY! TGIF = weekend is coming! 🎊"
        },
        {
          type: "order",
          q: "Complete the week:",
          words: ["Thursday", "Friday", "Saturday", "Sunday"],
          a: "Thursday Friday Saturday Sunday",
          tip: "The last four days of the week!"
        }
      ]
    },

    // ===================================================================
    // SET 2: MONTHS & SEASONS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u2-s2",
      title: "Set 2: Months & Seasons",
      questions: [
        {
          type: "match",
          q: "Match the months to their abbreviations:",
          pairs: [
            { left: "January", right: "Jan" },
            { left: "February", right: "Feb" },
            { left: "December", right: "Dec" }
          ],
          a: "completed",
          successtip: "✓ Month abbreviations are used everywhere! 📅"
        },
        {
          type: "mc",
          q: "The first month of the year is:",
          options: ["January", "December", "March"],
          a: "January",
          successtip: "✓ January 1st = New Year's Day! 🎆"
        },
        {
          type: "input",
          q: "The month with Valentine's Day is ___.",
          a: "February",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ FEBRUARY 14th = Valentine's Day! 💝"
        },
        {
          type: "mc",
          q: "Which month comes after October?",
          options: ["November", "September", "December"],
          a: "November",
          successtip: "✓ Oct → Nov → Dec! End of the year! 🍂"
        },
        {
          type: "match",
          q: "Match the seasons (Northern Hemisphere):",
          pairs: [
            { left: "Spring", right: "March-May" },
            { left: "Summer", right: "June-August" },
            { left: "Winter", right: "December-February" }
          ],
          a: "completed",
          successtip: "✓ Seasons change every 3 months! 🌸☀️❄️"
        },
        {
          type: "input",
          q: "The month when summer starts (June, July, or August)? ___",
          a: "June",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ JUNE! Summer begins in June! ☀️"
        },
        {
          type: "mc",
          q: "Which month has Halloween?",
          options: ["October", "November", "September"],
          a: "October",
          successtip: "✓ October 31st = Halloween! 🎃"
        },
        {
          type: "order",
          q: "Put the spring months in order:",
          words: ["March", "April", "May"],
          a: "March April May",
          tip: "Spring = Mar, Apr, May in Northern Hemisphere! 🌸"
        },
        {
          type: "input",
          q: "The last month of the year is ___.",
          a: "December",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ DECEMBER! Month of Christmas and New Year! 🎄"
        },
        {
          type: "mc",
          q: "Which season is coldest?",
          options: ["Winter", "Summer", "Spring"],
          a: "Winter",
          successtip: "✓ Winter = coldest season! ❄️⛄"
        }
      ]
    },

    // ===================================================================
    // SET 3: ORDINAL NUMBERS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u2-s3",
      title: "Set 3: Ordinal Numbers",
      questions: [
        {
          type: "match",
          q: "Match the numbers to ordinals:",
          pairs: [
            { left: "1", right: "1st (first)" },
            { left: "2", right: "2nd (second)" },
            { left: "3", right: "3rd (third)" }
          ],
          a: "completed",
          successtip: "✓ 1st, 2nd, 3rd are irregular! Memorize them! 🥇🥈🥉"
        },
        {
          type: "mc",
          q: "How do you write 'fourth'?",
          options: ["4th", "4st", "4rd"],
          a: "4th",
          successtip: "✓ Most ordinals end in -th! 4th, 5th, 6th... ✅"
        },
        {
          type: "input",
          q: "The ordinal for 5 is: ___",
          a: "fifth",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["5th"] },
          successtip: "✓ FIFTH or 5th! Both are correct! ✅"
        },
        {
          type: "mc",
          q: "Which is correct for 21?",
          options: ["21st (twenty-first)", "21th (twenty-oneth)", "21nd (twenty-onend)"],
          a: "21st (twenty-first)",
          successtip: "✓ 21st = twenty-FIRST! Same ending as 1st! 🎯"
        },
        {
          type: "match",
          q: "Match the ordinals:",
          pairs: [
            { left: "10th", right: "tenth" },
            { left: "20th", right: "twentieth" },
            { left: "30th", right: "thirtieth" }
          ],
          a: "completed",
          successtip: "✓ Notice: twenty → twentieth (change y to i)! 📝"
        },
        {
          type: "input",
          q: "How do you say 12th? ___",
          a: "twelfth",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["12th"] },
          successtip: "✓ TWELFTH! Irregular like 'twelve'! 🎯"
        },
        {
          type: "mc",
          q: "What comes after 'thirty-first'?",
          options: ["There's no 32nd day in a month", "Thirty-second", "Thirty-oneth"],
          a: "There's no 32nd day in a month",
          successtip: "✓ Months have maximum 31 days! 📅"
        },
        {
          type: "order",
          q: "Put in order:",
          words: ["first", "second", "third", "fourth"],
          a: "first second third fourth",
          tip: "The first four ordinals!"
        },
        {
          type: "input",
          q: "The ordinal for 8 is: ___",
          a: "eighth",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["8th"] },
          successtip: "✓ EIGHTH or 8th! Easy pattern! ✅"
        },
        {
          type: "mc",
          q: "Which ordinal is different from the pattern?",
          options: ["1st, 2nd, 3rd", "4th, 5th, 6th", "7th, 8th, 9th"],
          a: "1st, 2nd, 3rd",
          successtip: "✓ 1st, 2nd, 3rd are irregular! The rest follow -th pattern! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 4: WRITING DATES (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u2-s4",
      title: "Set 4: Date Formats",
      questions: [
        {
          type: "match",
          q: "Match the date formats:",
          pairs: [
            { left: "US format", right: "May 10, 2024" },
            { left: "UK format", right: "10th May 2024" },
            { left: "International", right: "10/05/2024 (day/month)" }
          ],
          a: "completed",
          successtip: "✓ US = month first, UK = day first! Important difference! 🇺🇸🇬🇧"
        },
        {
          type: "mc",
          q: "Your credit card expires on 12/25. In the US, this means:",
          options: ["December 2025", "December 25th", "25th December"],
          a: "December 2025",
          successtip: "✓ Credit cards show MM/YY format! 12/25 = December 2025! 💳"
        },
        {
          type: "input",
          q: "US format: March 15, 2024 is written as: ___/15/2024",
          a: "03",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["3", "3/"] },
          successtip: "✓ March = 03! Month comes first in US format! 🇺🇸"
        },
        {
          type: "mc",
          q: "Your medicine expires on 06/2026. What does this mean?",
          options: ["June 2026", "6th day of 2026", "2026 June 6"],
          a: "June 2026",
          successtip: "✓ Expiration dates = MM/YYYY (month/year)! 💊"
        },
        {
          type: "match",
          q: "Match the expiration dates to their meanings:",
          pairs: [
            { left: "EXP: 03/28", right: "March 2028" },
            { left: "USE BY: 10/2025", right: "October 2025" },
            { left: "BEST BEFORE: 12/27", right: "December 2027" }
          ],
          a: "completed",
          successtip: "✓ Expiration dates = MM/YY format everywhere! 📦"
        },
        {
          type: "input",
          q: "If today is January 10, 2024, write in US format: ___",
          a: "01/10/2024",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["1/10/2024", "01/10/24", "1/10/24"] },
          successtip: "✓ US = MM/DD/YYYY! Month/Day/Year! 🇺🇸"
        },
        {
          type: "mc",
          q: "Your doctor's appointment is 05/15/2024 (US format). What's the date?",
          options: ["May 15th, 2024", "15th May 2024", "May 5th, 2024"],
          a: "May 15th, 2024",
          successtip: "✓ 05/15/2024 = May 15th! Month first! 🏥"
        },
        {
          type: "order",
          q: "Arrange the US date format:",
          words: ["Month", "Day", "Year"],
          a: "Month Day Year",
          tip: "US always puts month first! MM/DD/YYYY 🇺🇸"
        },
        {
          type: "input",
          q: "UK format: 25th December 2024 is written numerically as: ___/12/2024",
          a: "25",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ UK = DD/MM/YYYY! Day first! 🇬🇧"
        },
        {
          type: "mc",
          q: "What does 'EXP 07/25' mean on food packaging?",
          options: ["Expires in July 2025", "Expires on 7th day of 25th month", "Expires in 25 weeks"],
          a: "Expires in July 2025",
          successtip: "✓ EXP = expiration date! MM/YY format! 🥫"
        }
      ]
    },

    // ===================================================================
    // SET 5: ASKING ABOUT DATES (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u2-s5",
      title: "Set 5: Talking About Dates",
      questions: [
        {
          type: "match",
          q: "Match the questions to topics:",
          pairs: [
            { left: "When is your birthday?", right: "Personal dates" },
            { left: "What's today's date?", right: "Current date" },
            { left: "When does school start?", right: "Event dates" }
          ],
          a: "completed",
          successtip: "✓ Different questions for different date topics! 🗓️"
        },
        {
          type: "mc",
          q: "A: 'When is your birthday?' B: '___'",
          options: ["It's on May 10th", "It's May", "It's 10"],
          a: "It's on May 10th",
          successtip: "✓ Use 'on' + full date for birthdays! 🎂"
        },
        {
          type: "input",
          q: "Q: 'When is Christmas?' A: 'It's on December ___.'",
          a: "25th",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["25", "twenty-fifth"] },
          successtip: "✓ Christmas = December 25th! 🎄"
        },
        {
          type: "mc",
          q: "How do you SAY the date 'March 3rd'?",
          options: ["March third", "March three", "Three March"],
          a: "March third",
          successtip: "✓ We WRITE '3rd' but SAY 'third'! 📝→🗣️"
        },
        {
          type: "order",
          q: "Put the conversation in order:",
          words: ["When", "is", "your", "appointment?"],
          a: "When is your appointment?",
          tip: "Asking about scheduled dates! 🏥"
        },
        {
          type: "input",
          q: "Q: 'What's the date today?' A: 'It's January ___ (15th).'",
          a: "15th",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["15", "fifteenth"] },
          successtip: "✓ Today's date = current day! 📅"
        },
        {
          type: "mc",
          q: "Independence Day in the US is ___.",
          options: ["July 4th", "July 14th", "June 4th"],
          a: "July 4th",
          successtip: "✓ July 4th = US Independence Day! 🇺🇸🎆"
        },
        {
          type: "match",
          q: "Match the holidays to their dates:",
          pairs: [
            { left: "New Year's Day", right: "January 1st" },
            { left: "Halloween", right: "October 31st" },
            { left: "Christmas", right: "December 25th" }
          ],
          a: "completed",
          successtip: "✓ Major holidays have fixed dates! 🎉"
        },
        {
          type: "input",
          q: "Q: 'When does summer start?' A: 'It starts in ___.'",
          a: "June",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ Summer starts in JUNE (Northern Hemisphere)! ☀️"
        },
        {
          type: "mc",
          q: "Valentine's Day is on ___.",
          options: ["February 14th", "February 4th", "March 14th"],
          a: "February 14th",
          successtip: "✓ February 14th = Valentine's Day! 💝"
        }
      ]
    },

    // ===================================================================
    // SET 6: SAYING YEARS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u2-s6",
      title: "Set 6: Pronouncing Years",
      questions: [
        {
          type: "match",
          q: "Match years to how we say them:",
          pairs: [
            { left: "1990", right: "nineteen ninety" },
            { left: "2005", right: "two thousand and five" },
            { left: "2015", right: "twenty fifteen" }
          ],
          a: "completed",
          successtip: "✓ Years have different pronunciation patterns! 📆"
        },
        {
          type: "mc",
          q: "How do you say '1985'?",
          options: ["nineteen eighty-five", "one thousand nine hundred eighty-five", "nineteen eight five"],
          a: "nineteen eighty-five",
          successtip: "✓ Split into pairs: 19-85 = nineteen eighty-five! 🎯"
        },
        {
          type: "input",
          q: "2024 is said as: twenty ___",
          a: "twenty-four",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["twenty four", "24"] },
          successtip: "✓ 2024 = twenty twenty-four! Modern way! 📱"
        },
        {
          type: "mc",
          q: "How do you say '2000'?",
          options: ["two thousand", "twenty hundred", "twenty zero-zero"],
          a: "two thousand",
          successtip: "✓ Year 2000 = the year two thousand! 🎆"
        },
        {
          type: "match",
          q: "Match the decades:",
          pairs: [
            { left: "1980s", right: "the eighties" },
            { left: "1990s", right: "the nineties" },
            { left: "2000s", right: "the two thousands" }
          ],
          a: "completed",
          successtip: "✓ Decades = 'the eighties', 'the nineties'! 🎵"
        },
        {
          type: "input",
          q: "1776 (American Independence) is said as: seventeen ___",
          a: "seventy-six",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["seventy six", "76"] },
          successtip: "✓ 1776 = seventeen seventy-six! Historical date! 🇺🇸"
        },
        {
          type: "mc",
          q: "How do you say '2010'?",
          options: ["twenty ten", "two thousand and ten", "Both are correct"],
          a: "Both are correct",
          successtip: "✓ 2010 can be 'twenty ten' OR 'two thousand and ten'! Both work! ✅"
        },
        {
          type: "order",
          q: "Arrange to say 1995:",
          words: ["nineteen", "ninety-five"],
          a: "nineteen ninety-five",
          tip: "Split years into pairs for 1900s! 19-95!"
        },
        {
          type: "input",
          q: "2001 is usually said as: two thousand and ___",
          a: "one",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 2001 = two thousand and one! Like the movie! 🎬"
        },
        {
          type: "mc",
          q: "The year 1969 (Moon landing) is said as:",
          options: ["nineteen sixty-nine", "one thousand nine hundred sixty-nine", "nineteen six nine"],
          a: "nineteen sixty-nine",
          successtip: "✓ 1969 = nineteen sixty-nine! Man on the moon! 🌙👨‍🚀"
        }
      ]
    },

    // ===================================================================
    // SET 7: INTRODUCING 'WAS BORN' (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u2-s7",
      title: "Set 7: Birth Dates",
      questions: [
        {
          type: "match",
          q: "Match the famous people to their birth years (READ ONLY):",
          pairs: [
            { left: "Steve Jobs was born in 1955", right: "Apple founder" },
            { left: "Taylor Swift was born in 1989", right: "Pop star" },
            { left: "Lionel Messi was born in 1987", right: "Soccer player" }
          ],
          a: "completed",
          tip: "'Was born' = nació. We use this to talk about birth dates! 👶"
        },
        {
          type: "mc",
          q: "READING: 'Albert Einstein was born in 1879.' When was Einstein born?",
          options: ["In 1879", "In 1987", "In 1789"],
          a: "In 1879",
          successtip: "✓ Einstein was born in 1879! 'Was born' = past tense! 🧠"
        },
        {
          type: "input",
          q: "READING: 'Shakespeare was born in 1564.' Fill in: Shakespeare ___ born in 1564.",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! 'Was born' is the correct form! 📚"
        },
        {
          type: "mc",
          q: "READING: 'My grandmother was born on June 5th, 1950.' When is her birthday?",
          options: ["June 5th", "June 1950", "1950"],
          a: "June 5th",
          successtip: "✓ Birthday = specific day (June 5th)! 🎂"
        },
        {
          type: "match",
          q: "Complete the sentences (READING):",
          pairs: [
            { left: "I was born in...", right: "1995 (year)" },
            { left: "She was born on...", right: "March 10th (date)" },
            { left: "They were born in...", right: "Mexico (place)" }
          ],
          a: "completed",
          tip: "Use 'in' for year/place, 'on' for specific date! 📍"
        },
        {
          type: "input",
          q: "READING: 'Mark Zuckerberg was born in 1984.' He ___ born in 1984.",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! Same pattern: 'was born'! 💻"
        },
        {
          type: "mc",
          q: "READING: 'J.K. Rowling was born on July 31st, 1965.' What's her birth date?",
          options: ["July 31st, 1965", "July 1965", "1965"],
          a: "July 31st, 1965",
          successtip: "✓ Full birth date = month + day + year! 📖✨"
        },
        {
          type: "order",
          q: "Arrange the sentence:",
          words: ["She", "was", "born", "in", "2000"],
          a: "She was born in 2000",
          tip: "Pattern: Subject + was born + in + year! 👶"
        },
        {
          type: "input",
          q: "PRACTICE: Complete: 'My brother ___ born in 2005.'",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WAS! He/she/it = was born! ✅"
        },
        {
          type: "mc",
          q: "READING: 'Frida Kahlo was born on July 6th, 1907 in Mexico.' Where was she born?",
          options: ["In Mexico", "In July", "In 1907"],
          a: "In Mexico",
          successtip: "✓ Born IN Mexico (place), ON July 6th (date)! 🎨🇲🇽"
        }
      ]
    },

    // ===================================================================
    // SET 8: USING 'AGO' (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u2-s8",
      title: "Set 8: Time Expressions - 'Ago'",
      questions: [
        {
          type: "match",
          q: "Match the time expressions:",
          pairs: [
            { left: "10 years ago", right: "2016 (if now is 2026)" },
            { left: "5 months ago", right: "August (if now is January)" },
            { left: "2 weeks ago", right: "Recent past" }
          ],
          a: "completed",
          tip: "'Ago' = means 'before now'. It goes at the END! ⏰"
        },
        {
          type: "mc",
          q: "READING: 'I was born 25 years ago.' This means I was born in ___ (current year is 2026).",
          options: ["2001", "2025", "1991"],
          a: "2001",
          successtip: "✓ 2026 - 25 = 2001! 'Ago' = back in time! 🔙"
        },
        {
          type: "input",
          q: "READING: 'The movie was released 10 years ago.' If now is 2026, it was released in ___.",
          a: "2016",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 2016! Subtract: 2026 - 10 = 2016! 🎬"
        },
        {
          type: "mc",
          q: "READING: 'My sister was born 15 years ago.' If now is 2026, she was born in:",
          options: ["2011", "2010", "2015"],
          a: "2011",
          successtip: "✓ 2026 - 15 = 2011! Simple math! 🧮"
        },
        {
          type: "match",
          q: "Match 'ago' expressions to their patterns:",
          pairs: [
            { left: "3 days ago", right: "Short time" },
            { left: "50 years ago", right: "Long time" },
            { left: "2 hours ago", right: "Very recent" }
          ],
          a: "completed",
          successtip: "✓ 'Ago' works with days, weeks, months, years, hours! ⏱️"
        },
        {
          type: "input",
          q: "PRACTICE: 'I graduated 5 years ___.'",
          a: "ago",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ AGO! It goes at the end: 5 years AGO! 🎓"
        },
        {
          type: "mc",
          q: "READING: 'The pyramids were built 4,500 years ago.' Approximately when?",
          options: ["2500 BCE", "4500 CE", "500 BCE"],
          a: "2500 BCE",
          successtip: "✓ 2026 - 4,500 ≈ 2474 (about 2500 BCE)! Ancient history! 🏛️"
        },
        {
          type: "order",
          q: "Arrange the sentence:",
          words: ["I", "started", "school", "15", "years", "ago"],
          a: "I started school 15 years ago",
          tip: "Pattern: Event + number + time + AGO! 📚"
        },
        {
          type: "input",
          q: "READING: 'She moved to Canada 10 years ago.' If now is 2026, she moved in ___.",
          a: "2016",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 2016! Always subtract to find the year! 🇨🇦"
        },
        {
          type: "mc",
          q: "READING: 'World War II ended 81 years ago.' If now is 2026, it ended in:",
          options: ["1945", "1944", "1950"],
          a: "1945",
          successtip: "✓ 1945! WWII ended in 1945! 🕊️"
        }
      ]
    },

    // ===================================================================
    // SET 9: HISTORICAL EVENTS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u2-s9",
      title: "Set 9: Important Dates",
      questions: [
        {
          type: "match",
          q: "Match historical events to their years:",
          pairs: [
            { left: "First Moon landing", right: "1969" },
            { left: "Fall of Berlin Wall", right: "1989" },
            { left: "First iPhone released", right: "2007" }
          ],
          a: "completed",
          tip: "These are famous historical dates! 🌍"
        },
        {
          type: "mc",
          q: "READING: 'The Titanic sank on April 15, 1912.' When did this happen?",
          options: ["April 15, 1912", "April 1912", "1912"],
          a: "April 15, 1912",
          successtip: "✓ April 15, 1912! Tragic historical event! 🚢"
        },
        {
          type: "input",
          q: "READING: 'World War I ended in 1918.' If now is 2026, that was ___ years ago.",
          a: "108",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 108! Calculate: 2026 - 1918 = 108 years ago! 🕊️"
        },
        {
          type: "mc",
          q: "READING: 'The internet became public in 1991.' If now is 2026, how many years ago?",
          options: ["35 years ago", "25 years ago", "45 years ago"],
          a: "35 years ago",
          successtip: "✓ 35 years! 2026 - 1991 = 35! The internet changed everything! 🌐"
        },
        {
          type: "match",
          q: "Match events to their significance:",
          pairs: [
            { left: "July 20, 1969", right: "Moon landing date" },
            { left: "September 11, 2001", right: "9/11 attacks" },
            { left: "December 25", right: "Christmas" }
          ],
          a: "completed",
          successtip: "✓ These dates are remembered worldwide! 🌎"
        },
        {
          type: "input",
          q: "READING: 'The COVID-19 pandemic started in 2019.' If now is 2026, that was ___ years ago.",
          a: "7",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 7 years! 2026 - 2019 = 7! Recent history! 🦠"
        },
        {
          type: "mc",
          q: "READING: 'Mexico gained independence on September 16, 1821.' What's the date?",
          options: ["September 16", "September 1821", "16th century"],
          a: "September 16",
          successtip: "✓ September 16th = Mexican Independence Day! 🇲🇽🎉"
        },
        {
          type: "order",
          q: "Arrange the facts:",
          words: ["The", "first", "computer", "was", "made", "in", "1946"],
          a: "The first computer was made in 1946",
          tip: "ENIAC = first computer! 💻"
        },
        {
          type: "input",
          q: "READING: 'Nelson Mandela was released from prison in 1990.' If now is 2026, that was ___ years ago.",
          a: "36",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 36 years! A historic moment! ✊"
        },
        {
          type: "mc",
          q: "READING: 'The first Star Wars movie came out in 1977.' If now is 2026, how long ago?",
          options: ["49 years ago", "39 years ago", "59 years ago"],
          a: "49 years ago",
          successtip: "✓ 49 years! Star Wars changed cinema! 🎬⭐"
        }
      ]
    },

    // ===================================================================
    // SET 10: CALCULATING TIME (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u2-s10",
      title: "Set 10: Time Calculations",
      questions: [
        {
          type: "match",
          q: "Calculate: If now is 2026...",
          pairs: [
            { left: "Born in 2000", right: "26 years old" },
            { left: "Born in 1990", right: "36 years old" },
            { left: "Born in 1985", right: "41 years old" }
          ],
          a: "completed",
          successtip: "✓ Age = current year - birth year! 🧮"
        },
        {
          type: "mc",
          q: "CHALLENGE: If someone was born 30 years ago and now is 2026, they were born in:",
          options: ["1996", "1995", "2000"],
          a: "1996",
          successtip: "✓ 2026 - 30 = 1996! Reverse calculation! 🔄"
        },
        {
          type: "input",
          q: "If a building was constructed in 1950 and now is 2026, it's ___ years old.",
          a: "76",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 76! Subtract: 2026 - 1950 = 76! 🏢"
        },
        {
          type: "mc",
          q: "Your passport expires in 2030. If now is 2026, it expires in ___ years.",
          options: ["4 years", "3 years", "5 years"],
          a: "4 years",
          successtip: "✓ 4 years! 2030 - 2026 = 4! Keep track! 🛂"
        },
        {
          type: "match",
          q: "Time from now (2026):",
          pairs: [
            { left: "In 5 years", right: "2031 (future)" },
            { left: "5 years ago", right: "2021 (past)" },
            { left: "10 years from now", right: "2036 (future)" }
          ],
          a: "completed",
          successtip: "✓ 'Ago' = past, 'In/from now' = future! ⏰"
        },
        {
          type: "input",
          q: "If you're 25 years old in 2026, you were born in ___.",
          a: "2001",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 2001! 2026 - 25 = 2001! ✅"
        },
        {
          type: "mc",
          q: "A car made in 2015. If now is 2026, the car is ___ years old.",
          options: ["11 years old", "10 years old", "12 years old"],
          a: "11 years old",
          successtip: "✓ 11 years! 2026 - 2015 = 11! 🚗"
        },
        {
          type: "order",
          q: "Arrange the logic:",
          words: ["Current", "year", "minus", "birth", "year", "equals", "age"],
          a: "Current year minus birth year equals age",
          tip: "This is the AGE formula! 📐"
        },
        {
          type: "input",
          q: "If your credit card expires in 3 years and now is 2026, it expires in ___.",
          a: "2029",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 2029! Add: 2026 + 3 = 2029! 💳"
        },
        {
          type: "mc",
          q: "FINAL CHALLENGE: Someone born on June 15, 1995. In 2026, how old are they?",
          options: ["31 years old", "34 years old", "28 years old"],
          a: "31 years old",
          successtip: "✓ 31! 2026 - 1995 = 31! You mastered dates! 🎉🏆"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (10 questions)
  // ===================================================================
 // ===================================================================
// READING SET: Birthday Party Invitation
// ===================================================================
readingSet: {
  id: "m5-u2-reading",
  title: "Reading: Birthday Party Invitation",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Invitation Card (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**You're Invited!**

Join us for Emma's 18th Birthday Party!
**Date:** Saturday, July 15th, 2026
**Time:** 7:00 PM
**Place:** 123 Park Avenue
**RSVP:** by July 10th`
    },
    {
      type: "mc",
      q: "When is Emma's birthday party?",
      options: ["July 15th, 2026", "July 10th, 2026", "July 7th, 2026"],
      a: "July 15th, 2026",
      successtip: "✓ July 15th! Read carefully! 🎂"
    },
    {
      type: "mc",
      q: "What day of the week is the party?",
      options: ["Saturday", "Sunday", "Friday"],
      a: "Saturday",
      successtip: "✓ Saturday! Weekend party! 🎉"
    },
    {
      type: "input",
      q: "How old is Emma turning?",
      a: "18",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["eighteen"] },
      successtip: "✓ 18! Her 18th birthday! 🎂"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Logistics & Time (preguntas 4-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Logistics**

The celebration will be held in the evening at a central location on Park Avenue. Make sure to arrive on time to see the birthday girl!

**Time:** 7:00 PM
**Address:** 123 Park Avenue`
    },
    {
      type: "mc",
      q: "What time does the party start?",
      options: ["7:00 PM", "10:00 AM", "7:00 AM"],
      a: "7:00 PM",
      successtip: "✓ 7 PM = evening party! 🌙"
    },
    {
      type: "input",
      q: "The party is at ___ Park Avenue.",
      a: "123",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ 123 Park Avenue! Address details! 📍"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Response Needed (preguntas 6-8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Action Required**

Please let us know if you can attend. We need to confirm the final guest list a few days before the event. 

**RSVP by July 10th**`
    },
    {
      type: "input",
      q: "You need to RSVP by July ___.",
      a: "10th",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["10", "tenth"] },
      successtip: "✓ July 10th! RSVP = respond before this date! 📧"
    },
    {
      type: "mc",
      q: "RSVP means:",
      options: ["Please respond", "Come to party", "Bring a gift"],
      a: "Please respond",
      successtip: "✓ RSVP = French 'Répondez s'il vous plaît' (please respond)! 💌"
    },
    {
      type: "match",
      q: "Match the invitation details:",
      pairs: [
        { left: "Party date", right: "July 15th" },
        { left: "Emma's age", right: "18" },
        { left: "RSVP deadline", right: "July 10th" }
      ],
      a: "completed",
      successtip: "✓ All details matched! 📋"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Party Logic (preguntas 9-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Thinking Ahead**

Since Emma is celebrating a major milestone this year (2026), her family is already looking at old photos from the year she was born. Today is **July 1st, 2026**, so there isn't much time left to get a gift!`
    },
    {
      type: "mc",
      q: "If Emma is turning 18 in 2026, what year was she born?",
      options: ["2008", "2007", "2009"],
      a: "2008",
      successtip: "✓ 2008! 2026 - 18 = 2008! 🧮"
    },
    {
      type: "mc",
      q: "If today is July 1st, 2026, how many days until the party (July 15th)?",
      options: ["14 days", "15 days", "10 days"],
      a: "14 days",
      successtip: "✓ 14 days! July 15 - July 1 = 14! ⏰"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u2-review",
    title: "Unit 2 Review: Dates & Calendar",
    questions: [
      {
        type: "match",
        q: "Match the date vocabulary:",
        pairs: [
          { left: "Days of week", right: "Monday-Sunday" },
          { left: "Months", right: "January-December" },
          { left: "Ordinals", right: "1st, 2nd, 3rd..." }
        ],
        a: "completed",
        successtip: "✓ Core date vocabulary! 📅"
      },
      {
        type: "mc",
        q: "How do you write the ordinal for 21?",
        options: ["21st", "21th", "21nd"],
        a: "21st",
        successtip: "✓ 21st = twenty-first! Same ending as 1st! ✅"
      },
      {
        type: "input",
        q: "What day comes after Thursday?",
        a: "Friday",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ FRIDAY! TGIF! 🎉"
      },
      {
        type: "mc",
        q: "In US format, 05/15/2024 means:",
        options: ["May 15th, 2024", "15th May 2024", "5th day of 2024"],
        a: "May 15th, 2024",
        successtip: "✓ US = MM/DD/YYYY! Month first! 🇺🇸"
      },
      {
        type: "input",
        q: "Christmas is on December ___.",
        a: "25th",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["25", "twenty-fifth"] },
        successtip: "✓ December 25th! 🎄"
      },
      {
        type: "mc",
        q: "How do you say '1995'?",
        options: ["nineteen ninety-five", "one thousand nine ninety-five", "nineteen nine five"],
        a: "nineteen ninety-five",
        successtip: "✓ Split into pairs: 19-95! ✅"
      },
      {
        type: "order",
        q: "Arrange the sentence:",
        words: ["She", "was", "born", "in", "March"],
        a: "She was born in March",
        tip: "Pattern: was born + in + month/year! 👶"
      },
      {
        type: "input",
        q: "If something happened 10 years before now, it happened 10 years ___.",
        a: "ago",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ AGO! Always at the end! ⏰"
      },
      {
        type: "mc",
        q: "If someone is 30 years old in 2026, they were born in:",
        options: ["1996", "1995", "2000"],
        a: "1996",
        successtip: "✓ 1996! 2026 - 30 = 1996! 🧮"
      },
      {
        type: "mc",
        q: "FINAL: EXP 08/27 on food means it expires in:",
        options: ["August 2027", "8th day of 27th month", "27th August"],
        a: "August 2027",
        successtip: "✓ 🎉 PERFECTO! You mastered dates and calendar! 🏆📅"
      }
    ]
  }
};
